/**
 * Almari Connect section -- Gmail orders, Google Photos, MMT, Uber, Zomato.
 *
 * v0 ships in DEMO mode -- one tap imports realistic Indian-context mock
 * data, surfaces the connector status + counts, and lets the user preview
 * the first batch before commit. Real OAuth (PKCE proxy on CF Pages
 * Functions) lands in v1.1; the connectors are wired and the UI is final.
 *
 * Per-connector status is persisted to localStorage so the badges survive
 * reloads. Imported batches publish a `almari:connector_imported` custom
 * event for downstream surfaces (capsule-grid, wishlist, value-spotlight).
 */
import { ALMARI_CONNECTORS, fetchGmailOrders, fetchClosetPhotos, fetchFoodOrders, fetchTrips, fetchTravel } from '../connectors/lifestyle/index.ts';
import type { ConnectMeta, ConnectorId, WardrobeOrderItem, PhotoSlim, FoodOrder, TripRecord, TravelBooking } from '../connectors/lifestyle/index.ts';

interface ConnectorBadge {
  status: 'idle' | 'connected';
  last_sync_ts?: number;
  count?: number;
}

const STORAGE_KEY = 'almari:connectors:v0';

type PreviewKind =
  | { kind: 'orders'; rows: readonly WardrobeOrderItem[] }
  | { kind: 'photos'; rows: readonly PhotoSlim[] }
  | { kind: 'food'; rows: readonly FoodOrder[] }
  | { kind: 'trips'; rows: readonly TripRecord[] }
  | { kind: 'travel'; rows: readonly TravelBooking[] };

export class AlmariConnectSection extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #state: Record<ConnectorId, ConnectorBadge> = loadState();
  #preview: { id: ConnectorId; data: PreviewKind } | null = null;
  #busy: ConnectorId | null = null;

  connectedCallback(): void {
    this.#render();
  }

  async #connect(id: ConnectorId): Promise<void> {
    if (this.#busy) return;
    this.#busy = id;
    this.#render();
    try {
      const data = await runConnector(id);
      this.#preview = { id, data };
      this.#state = {
        ...this.#state,
        [id]: { status: 'connected', last_sync_ts: Date.now(), count: data.rows.length },
      };
      saveState(this.#state);
      this.dispatchEvent(new CustomEvent('almari:connector_imported', { detail: { id, data }, bubbles: true, composed: true }));
      window.dispatchEvent(new CustomEvent('almari:connector_imported', { detail: { id, data } }));
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      console.warn(`[almari] connector ${id} failed:`, msg);
    } finally {
      this.#busy = null;
      this.#render();
    }
  }

  #disconnect(id: ConnectorId): void {
    this.#state = { ...this.#state, [id]: { status: 'idle' } };
    saveState(this.#state);
    if (this.#preview?.id === id) this.#preview = null;
    this.#render();
  }

  #render(): void {
    const cards = ALMARI_CONNECTORS.map((c) => this.#card(c)).join('');
    const preview = this.#preview ? this.#renderPreview(this.#preview) : '';
    this.#shadow.innerHTML = `
      <style>${STYLES}</style>
      <section class="connect" aria-labelledby="connect-title">
        <header>
          <h2 id="connect-title">Bring almari to life</h2>
          <p>Aapke purane orders, photos, aur safar — almari mein khud-ba-khud aa jaayenge. Sab encrypted, sab on-device.</p>
        </header>
        <ul class="grid">${cards}</ul>
        ${preview}
        <p class="note">Demo mode active. v1.1 ships full OAuth via Cloudflare Pages Function PKCE proxy. Aapke creds chassis-encrypted, refresh-token never plaintext.</p>
      </section>
    `;
    this.#wire();
  }

  #wire(): void {
    this.#shadow.querySelectorAll<HTMLButtonElement>('[data-connect]').forEach((btn) => {
      btn.addEventListener('click', () => this.#connect(btn.dataset.connect as ConnectorId));
    });
    this.#shadow.querySelectorAll<HTMLButtonElement>('[data-disconnect]').forEach((btn) => {
      btn.addEventListener('click', () => this.#disconnect(btn.dataset.disconnect as ConnectorId));
    });
    this.#shadow.querySelector('[data-close-preview]')?.addEventListener('click', () => {
      this.#preview = null;
      this.#render();
    });
  }

  #card(meta: ConnectMeta): string {
    const s = this.#state[meta.id] ?? { status: 'idle' };
    const busy = this.#busy === meta.id;
    const connected = s.status === 'connected';
    const badge = connected
      ? `<span class="badge ok">Connected · ${s.count ?? 0} items</span>`
      : `<span class="badge dim">Not connected</span>`;
    const action = busy
      ? `<button class="btn busy" disabled>Importing…</button>`
      : connected
        ? `<button class="btn ghost" data-disconnect="${meta.id}">Disconnect</button>`
        : `<button class="btn primary" data-connect="${meta.id}">Connect (Demo)</button>`;
    const titleHi = meta.title_hi ? `<span class="devanagari">${meta.title_hi}</span>` : '';
    return `
      <li class="card" data-id="${meta.id}">
        <header>
          <h3>${meta.title_en} ${titleHi}</h3>
          ${badge}
        </header>
        <p>${meta.description}</p>
        <footer>
          <span class="purpose">${meta.purposes[0]?.split('.').pop() ?? ''}</span>
          ${action}
        </footer>
      </li>
    `;
  }

  #renderPreview(p: { id: ConnectorId; data: PreviewKind }): string {
    const rows: string[] = [];
    if (p.data.kind === 'orders') {
      for (const o of p.data.rows.slice(0, 6)) {
        const it = o.items[0];
        rows.push(`<tr><td>${o.merchant}</td><td>${it?.title ?? '(no title)'}</td><td>${it?.brand ?? ''}</td><td>${o.order_date}</td><td>${fmt(o.total.paise / 100)}</td></tr>`);
      }
      return `<aside class="preview"><header><h4>Preview · Gmail orders (${p.data.rows.length})</h4><button data-close-preview>×</button></header><table><thead><tr><th>Source</th><th>Item</th><th>Brand</th><th>Date</th><th>Paid</th></tr></thead><tbody>${rows.join('')}</tbody></table></aside>`;
    }
    if (p.data.kind === 'photos') {
      for (const ph of p.data.rows.slice(0, 6)) {
        rows.push(`<tr><td>${ph.external_id}</td><td>${ph.camera_model ?? ''}</td><td>${ph.captured_at?.slice(0, 10) ?? ''}</td><td>${ph.mime}</td></tr>`);
      }
      return `<aside class="preview"><header><h4>Preview · Google Photos (${p.data.rows.length})</h4><button data-close-preview>×</button></header><table><thead><tr><th>ID</th><th>Camera</th><th>Captured</th><th>Mime</th></tr></thead><tbody>${rows.join('')}</tbody></table></aside>`;
    }
    if (p.data.kind === 'food') {
      for (const f of p.data.rows.slice(0, 6)) {
        rows.push(`<tr><td>${f.merchant}</td><td>${f.restaurant ?? ''}</td><td>${f.delivery_window ?? ''}</td><td>${fmt(f.total.paise / 100)}</td></tr>`);
      }
      return `<aside class="preview"><header><h4>Preview · Zomato + Swiggy (${p.data.rows.length})</h4><button data-close-preview>×</button></header><table><thead><tr><th>Source</th><th>Restaurant</th><th>Window</th><th>Total</th></tr></thead><tbody>${rows.join('')}</tbody></table></aside>`;
    }
    if (p.data.kind === 'trips') {
      for (const t of p.data.rows.slice(0, 6)) {
        rows.push(`<tr><td>${t.merchant}</td><td>${t.origin}</td><td>${t.destination}</td><td>${fmt(t.fare.paise / 100)}</td></tr>`);
      }
      return `<aside class="preview"><header><h4>Preview · Rides (${p.data.rows.length})</h4><button data-close-preview>×</button></header><table><thead><tr><th>Source</th><th>From</th><th>To</th><th>Fare</th></tr></thead><tbody>${rows.join('')}</tbody></table></aside>`;
    }
    for (const t of p.data.rows.slice(0, 6)) {
      rows.push(`<tr><td>${t.merchant}</td><td>${t.kind}</td><td>${t.destination}</td><td>${fmt(t.cost.paise / 100)}</td></tr>`);
    }
    return `<aside class="preview"><header><h4>Preview · Travel bookings (${p.data.rows.length})</h4><button data-close-preview>×</button></header><table><thead><tr><th>Source</th><th>Kind</th><th>Destination</th><th>Cost</th></tr></thead><tbody>${rows.join('')}</tbody></table></aside>`;
  }
}

async function runConnector(id: ConnectorId): Promise<PreviewKind> {
  if (id === 'gmail-orders') return { kind: 'orders', rows: await fetchGmailOrders({ mode: 'mock' }) };
  if (id === 'gphotos') return { kind: 'photos', rows: await fetchClosetPhotos({ mode: 'mock' }) };
  if (id === 'zomato-swiggy') return { kind: 'food', rows: await fetchFoodOrders({ mode: 'mock' }) };
  if (id === 'uber-ola') return { kind: 'trips', rows: await fetchTrips({ mode: 'mock' }) };
  if (id === 'mmt-travel') return { kind: 'travel', rows: await fetchTravel({ mode: 'mock' }) };
  throw new Error(`unknown connector: ${id}`);
}

function fmt(n: number): string {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);
}

function loadState(): Record<ConnectorId, ConnectorBadge> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<ConnectorId, ConnectorBadge>) : ({} as Record<ConnectorId, ConnectorBadge>);
  } catch { return {} as Record<ConnectorId, ConnectorBadge>; }
}
function saveState(state: Record<ConnectorId, ConnectorBadge>): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* swallow */ }
}

const STYLES = `
:host { display: block; container-type: inline-size; }
.connect {
  --gold: oklch(83% 0.08 75);
  --paper: oklch(98% 0.012 92);
  --ink: oklch(22% 0.02 60);
  --line: color-mix(in oklch, var(--ink) 12%, transparent);
  background: var(--paper);
  color: var(--ink);
  padding: 2.4rem clamp(1rem, 3vw, 2.4rem);
  border-block: 1px solid var(--line);
  font-family: 'EB Garamond', 'Tiro Devanagari Hindi', Georgia, serif;
}
header h2 {
  font-size: clamp(1.6rem, 3.2vw, 2.4rem);
  font-weight: 500;
  letter-spacing: -0.01em;
  margin: 0 0 0.4rem;
}
header p { margin: 0 0 1.6rem; opacity: 0.78; max-width: 56ch; }
.grid {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 24rem), 1fr));
  gap: 1rem;
}
.card {
  position: relative;
  padding: 1.2rem 1.2rem 1.05rem;
  background: oklch(100% 0 0 / 0.5);
  border: 1px solid var(--line);
  border-radius: 6px;
  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 0.7rem;
  transition: box-shadow 280ms cubic-bezier(0.2, 0.6, 0.2, 1), translate 200ms;
}
.card:hover {
  box-shadow: 0 2px 24px -8px color-mix(in oklch, var(--gold) 60%, transparent);
  translate: 0 -1px;
}
.card header { display: flex; justify-content: space-between; align-items: baseline; gap: 0.6rem; }
.card h3 { margin: 0; font-size: 1.05rem; font-weight: 600; letter-spacing: -0.005em; }
.devanagari { font-family: 'Tiro Devanagari Hindi', serif; opacity: 0.78; font-weight: 400; font-size: 0.92rem; margin-inline-start: 0.4rem; }
.card p { margin: 0; font-size: 0.9rem; line-height: 1.45; opacity: 0.82; }
.card footer { display: flex; justify-content: space-between; align-items: center; gap: 0.5rem; padding-block-start: 0.4rem; border-block-start: 1px dashed var(--line); }
.purpose {
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  opacity: 0.55;
}
.badge { font-size: 0.7rem; letter-spacing: 0.06em; text-transform: uppercase; padding: 0.18rem 0.55rem; border-radius: 999px; font-family: 'JetBrains Mono', monospace; }
.badge.ok { background: color-mix(in oklch, var(--gold) 40%, transparent); color: oklch(35% 0.06 70); }
.badge.dim { background: color-mix(in oklch, var(--ink) 8%, transparent); opacity: 0.6; }
.btn {
  font: inherit;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  border: 1px solid currentColor;
  background: transparent;
  cursor: pointer;
  font-size: 0.85rem;
  letter-spacing: 0.02em;
  transition: background 180ms, color 180ms;
}
.btn.primary {
  background: var(--ink);
  color: var(--paper);
  border-color: var(--ink);
}
.btn.primary:hover { background: oklch(28% 0.04 60); }
.btn.ghost { color: var(--ink); opacity: 0.75; }
.btn.ghost:hover { opacity: 1; }
.btn.busy { opacity: 0.7; cursor: progress; }
.preview {
  margin-block-start: 1.4rem;
  padding: 1.1rem 1.2rem;
  background: oklch(100% 0 0 / 0.7);
  border: 1px solid var(--line);
  border-radius: 6px;
}
.preview header { display: flex; justify-content: space-between; align-items: center; margin-block-end: 0.7rem; }
.preview h4 { margin: 0; font-size: 1rem; font-weight: 600; }
.preview button { background: transparent; border: none; cursor: pointer; font-size: 1.2rem; opacity: 0.7; }
.preview button:hover { opacity: 1; }
.preview table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
.preview th, .preview td { padding: 0.35rem 0.5rem; text-align: start; border-block-end: 1px solid var(--line); }
.preview th { font-family: 'JetBrains Mono', monospace; font-size: 0.72rem; letter-spacing: 0.06em; text-transform: uppercase; opacity: 0.6; font-weight: 500; }
.note { margin: 1.1rem 0 0; font-size: 0.78rem; opacity: 0.62; max-width: 64ch; }
@container (min-width: 70rem) {
  .connect { padding-inline: 3.2rem; }
}
`;

if (!customElements.get('almari-connect-section')) {
  customElements.define('almari-connect-section', AlmariConnectSection);
}
